'use client'
import React, { useState, useRef, useCallback, useEffect } from 'react'
import { DatesType } from './types'
import DayPicker from 'react-day-picker'
import { useTranslations } from 'next-intl'
import { useSelectedDays } from './useSelectedDays'
import { useHandleClickOutside } from './useHandleClickOutside'
import { useDayClickHandler } from './useDayClickHandler'
import { useModifies } from './useModifies'
import styles from './styles.module.scss'
import './date-picker-styles.scss'
import DatePickerInput from './DatePickerInput'
import {
  useLocaleMonths,
  useWeekdaysLong,
  useWeekdaysShort,
} from './useLocalesForDatePicker'

interface RangeDatePickerProps {
  onChange: (dates: DatesType) => void
  values?: DatesType
}

const DatePickerRange: React.FC<RangeDatePickerProps> = ({
  onChange,
  values,
}) => {
  const [isShown, setIsShown] = useState(false)
  const [dates, setDates] = useState<DatesType>({
    from: undefined,
    to: undefined,
  })
  const t = useTranslations('translation')

  const [enteredTo, setEnteredTo] = useState<Date | undefined>(undefined)
  const pickerModalRef = useRef() as React.RefObject<HTMLDivElement>

  const onDayPickerShow = useCallback(() => setIsShown(true), [])
  const onDayPickerHide = useCallback(() => setIsShown(false), [])

  const onResetInputClick = useCallback(
    (type: 'from' | 'to') => {
      setDates({ ...dates, [type]: undefined })
      values && onChange({ ...dates, [type]: undefined })
    },
    [dates, onChange, values],
  )

  const handleClickOutside = useHandleClickOutside({
    pickerModalRef,
    action: onDayPickerHide,
  })

  useEffect(() => {
    values && setDates(values)
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const { from, to } = dates
    if (from && to) onChange({ from, to })
    // eslint-disable-next-line
  }, [dates])

  const onDayClickHandler = useDayClickHandler({
    dates,
    setDates,
    onDayPickerHide,
    setEnteredTo,
  })

  const selectedDays = useSelectedDays({ dates, enteredTo })

  const months = useLocaleMonths()
  const weekdaysLong = useWeekdaysLong()
  const weekdaysShort = useWeekdaysShort()

  const modifiers = useModifies({ dates, enteredTo })

  const onDayEnter = useCallback(
    (enteredDate: Date) => {
      const { from, to } = dates

      if (from || to) {
        setEnteredTo(enteredDate)
      }
    },
    [dates],
  )

  return (
    <div>
      <>
        <div className={styles.rangeInputWrapper}>
          <DatePickerInput
            label={t('dateFrom')}
            onClick={onDayPickerShow}
            value={dates.from}
            onResetClick={() => onResetInputClick('from')}
          />

          <div className={styles.dash}>-</div>

          <DatePickerInput
            label={t('dateTo')}
            onClick={onDayPickerShow}
            value={dates.to}
            onResetClick={() => onResetInputClick('to')}
          />
        </div>

        <div className="dateRangePickerOverlayWrapper">
          {isShown && (
            <div ref={pickerModalRef}>
              <DayPicker
                months={months}
                weekdaysLong={weekdaysLong}
                weekdaysShort={weekdaysShort}
                firstDayOfWeek={1}
                selectedDays={selectedDays}
                onDayClick={onDayClickHandler}
                numberOfMonths={1}
                modifiers={modifiers}
                onDayMouseEnter={onDayEnter}
              />
            </div>
          )}
        </div>
      </>
    </div>
  )
}

export default DatePickerRange
